import { useEffect } from "react";

/** Plate drift, px, between the top and the bottom of the viewport. */
const PLATE_SHIFT = 18;

/** The full-bleed band is the page's one spread, so it carries the larger move. */
const BAND_SHIFT = 34;

/**
 * The guide's art drifting against the page as it scrolls. One listener for
 * every figure, deliberately: this page already runs three independent scroll
 * readers (ProgressBar, GuideHeaderMotion's tilt, GuideNav's observer), so a
 * fourth would be a fourth callback per frame, and one per figure would be worse
 * still.
 *
 * It writes `--parallax` in px and nothing else; the rule that consumes it is
 * `translate` on the figure, which leaves the image's own `transform` free for
 * its hover lift and is why no transition here can lag the scroll.
 *
 * Reduced motion opts the whole effect out rather than shortening it — this moves
 * pictures, unlike the header's tilt, which keeps its geometry. Leaving the
 * property unset is the entire fallback: every consumer reads
 * `var(--parallax, 0px)`, so with no JavaScript and with reduced motion alike the
 * art simply sits where it was authored.
 */
export function GuideParallax(): null {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const figures = Array.from(document.querySelectorAll<HTMLElement>(".atlas-guide-row > figure"));
    if (figures.length === 0) return undefined;
    let raf = 0;
    const update = (): void => {
      raf = 0;
      const vh = window.innerHeight;
      for (const figure of figures) {
        const rect = figure.getBoundingClientRect();
        // -1 at the viewport's top edge, +1 at the bottom, 0 when centred.
        const offset = Math.max(-1, Math.min(1, (rect.top + rect.height / 2 - vh / 2) / vh));
        const shift = figure.closest(".atlas-guide-row--bleed") ? BAND_SHIFT : PLATE_SHIFT;
        figure.style.setProperty("--parallax", `${(-offset * shift).toFixed(1)}px`);
      }
    };
    const onScroll = (): void => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return null;
}
